import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Collapse,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import {
  DEFAULT_MATCH_CONDITION,
  IBS_EARN_KEY_MAX_LENGTH,
  MATCH_FIELD_OPTIONS,
  MATCH_OP_OPTIONS,
  MATCH_VALUE_MAX_LENGTH,
  type MatchCondition,
  type MatchConfig,
  type MatchField,
  type MatchOp,
  REGEX_VALUE_MAX_LENGTH,
  createDefaultMatchConfig,
} from '~/types/loyaltyContract';

interface Props {
  /** The current matchConfig, or null when the section is disabled. */
  value: MatchConfig | null;
  /** Called with the next matchConfig, or null when the section is disabled. */
  onChange: (next: MatchConfig | null) => void;
  /** Field-keyed validation errors (see validateMatchConfig). */
  errors?: Record<string, string>;
  /** When true, render all inputs disabled (display-only). */
  readOnly?: boolean;
  /**
   * The rule's exact identifier anchor field (e.g. `earnKey`). Used only to warn
   * when a condition targets the same field — the anchor already pins that field
   * to an exact value, so such a condition can't broaden matching.
   */
  anchorField?: MatchField;
}

/**
 * Visual builder for a custom rule `matchConfig`. Gated behind an enable toggle
 * (off by default) to conserve dialog space. Renders an IBS Earn Key field, an
 * AND/OR combinator (when >1 condition), and repeatable field/operator/value
 * condition rows. All edits flow through `onChange` with immutably-rebuilt config.
 *
 * @param props - See {@link Props}.
 */
export default function MatchConfigBuilder({ value, onChange, errors = {}, readOnly = false, anchorField }: Props) {
  const enabled = value !== null;

  // Conditions that target the same field as the exact anchor are redundant: the
  // anchor already pins that field to one exact value, so they can't broaden
  // matching. Surface this as a non-blocking warning (de-duplicated).
  const overlappingFields = anchorField
    ? Array.from(new Set((value?.match ?? []).map((c) => c.field).filter((f) => f === anchorField)))
    : [];

  /** Toggles the whole section on/off, seeding a default config when turned on. */
  const handleToggle = (checked: boolean): void => {
    onChange(checked ? createDefaultMatchConfig() : null);
  };

  /** Updates a single top-level field of the config immutably. */
  const updateConfig = (patch: Partial<MatchConfig>): void => {
    if (!value) {
      return;
    }
    onChange({ ...value, ...patch });
  };

  /** Updates one condition by index immutably. */
  const updateCondition = (index: number, patch: Partial<MatchCondition>): void => {
    if (!value) {
      return;
    }
    const match = value.match.map((c, i) => (i === index ? { ...c, ...patch } : c));
    onChange({ ...value, match });
  };

  /** Appends a fresh empty condition. */
  const addCondition = (): void => {
    if (!value) {
      return;
    }
    onChange({ ...value, match: [...value.match, { ...DEFAULT_MATCH_CONDITION }] });
  };

  /** Removes the condition at index (kept disabled when only one remains). */
  const removeCondition = (index: number): void => {
    if (!value) {
      return;
    }
    onChange({ ...value, match: value.match.filter((_, i) => i !== index) });
  };

  return (
    <Box>
      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            disabled={readOnly}
            onChange={(e) => handleToggle(e.target.checked)}
            inputProps={{ 'aria-label': 'Enable advanced match config' }}
          />
        }
        label="Enable advanced match config"
      />

      <Collapse in={enabled} unmountOnExit>
        {value && (
          <Stack spacing={2} mt={1}>
            <Typography variant="body2" color="text.secondary">
              Layers a deeper filter on top of the exact identifier anchor. Every condition must pass (AND) or any
              condition may pass (OR).
            </Typography>

            {overlappingFields.length > 0 && (
              <Alert severity="warning">
                {overlappingFields.length === 1 ? 'A condition targets ' : 'Conditions target '}
                <strong>{overlappingFields.join(', ')}</strong>, which is already the rule&apos;s exact identifier
                anchor. The anchor pins that field to one exact value, so this condition won&apos;t broaden matching —
                it&apos;s redundant. Target a different field to actually filter further.
              </Alert>
            )}

            <TextField
              label="IBS Earn Key"
              value={value.ibsEarnKey}
              onChange={(e) => updateConfig({ ibsEarnKey: e.target.value })}
              error={!!errors.ibsEarnKey}
              helperText={errors.ibsEarnKey || 'Sent to IBS as EARNKEY when this rule matches'}
              inputProps={{ maxLength: IBS_EARN_KEY_MAX_LENGTH }}
              disabled={readOnly}
              required
              fullWidth
            />

            {value.match.length > 1 && (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Combine conditions
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  color="primary"
                  value={value.combine ?? 'AND'}
                  disabled={readOnly}
                  onChange={(_, next: 'AND' | 'OR' | null) => {
                    if (next) {
                      updateConfig({ combine: next });
                    }
                  }}
                >
                  <ToggleButton value="AND">AND</ToggleButton>
                  <ToggleButton value="OR">OR</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}

            {value.match.map((condition, index) => {
              const valueError = errors[`match.${index}.value`];
              const isRegex = condition.op === 'regex';
              return (
                <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                  <FormControl sx={{ minWidth: 140 }} disabled={readOnly}>
                    <InputLabel>Field</InputLabel>
                    <Select
                      label="Field"
                      value={condition.field}
                      onChange={(e: SelectChangeEvent<MatchField>) => updateCondition(index, { field: e.target.value })}
                    >
                      {MATCH_FIELD_OPTIONS.map((f) => (
                        <MenuItem key={f} value={f}>
                          {f}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl sx={{ minWidth: 130 }} disabled={readOnly}>
                    <InputLabel>Operator</InputLabel>
                    <Select
                      label="Operator"
                      value={condition.op}
                      onChange={(e: SelectChangeEvent<MatchOp>) => updateCondition(index, { op: e.target.value })}
                    >
                      {MATCH_OP_OPTIONS.map((o) => (
                        <MenuItem key={o} value={o}>
                          {o}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    label="Value"
                    value={condition.value}
                    onChange={(e) => updateCondition(index, { value: e.target.value })}
                    error={!!valueError}
                    helperText={
                      valueError ||
                      (isRegex ? `Regex, max ${REGEX_VALUE_MAX_LENGTH} chars` : `Max ${MATCH_VALUE_MAX_LENGTH} chars`)
                    }
                    inputProps={{ maxLength: isRegex ? REGEX_VALUE_MAX_LENGTH : MATCH_VALUE_MAX_LENGTH }}
                    disabled={readOnly}
                    fullWidth
                  />

                  <IconButton
                    aria-label="Remove condition"
                    onClick={() => removeCondition(index)}
                    disabled={readOnly || value.match.length <= 1}
                    sx={{ mt: 1 }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              );
            })}

            {!readOnly && (
              <Box>
                <Button variant="text" startIcon={<AddIcon />} onClick={addCondition}>
                  Add condition
                </Button>
              </Box>
            )}
          </Stack>
        )}
      </Collapse>
    </Box>
  );
}
